// Group flat per-tag count rows into per-subTheme buckets, tagging each
// bucket with its taxonomyRole so we can split into "thematic" vs "attribute"
// tabs. Tags without a subTheme fall under "Ungrouped" and are treated as
// thematic.
//
// THE COUNTING UNIT IS THE ANNOTATION — 1 annotation = 1 count, everywhere:
//   - A tag row's `count` is annotation-unit at source: the BE groups the
//     annotations collection by tag, so an annotation contributes 1 to each
//     distinct tag it carries.
//   - A subTheme's total is NOT the sum of its tag counts. An annotation
//     carrying two tags from the same subTheme would be counted twice that
//     way. It comes from `subThemeAnnotationCounts` — a separate aggregation
//     that rolls tags up to their subTheme and de-dupes before counting, so
//     an annotation contributes 1 to each distinct subTheme it touches.
//   - Every `percentage` (tag rows and subTheme rows alike) is stamped by the
//     BE as a share of ALL annotations in scope — actual prevalence ("X% of
//     the annotations in view carry this"). Shares don't sum to 100: one
//     annotation sits in several subThemes and carries several tags at once,
//     so the shares legitimately overlap.
const UNGROUPED_KEY = "__ungrouped__";
// The subTheme rollup aggregation runs with includePartialDimensions, so
// annotations whose tags carry no subTheme land in this BE sentinel bucket —
// the same annotations whose tags we file under "Ungrouped" here.
const NA_BUCKET = "(N/A)";

export const groupAnnotationDataBySubTheme = ({
  annotationData,
  subThemeAnnotationCounts,
  tagDocs,
  subThemeDocs,
}) => {
  if (!Array.isArray(annotationData) || !annotationData.length) return [];
  if (!tagDocs?.length) return [];

  const subThemeDisplayMap = {};
  const subThemeRoleMap = {};
  subThemeDocs.forEach((st) => {
    subThemeDisplayMap[st._id] = st.display;
    subThemeRoleMap[st._id] =
      st.meta?.taxonomyRole === "attribute" ? "attribute" : "thematic";
  });
  const tagDocMap = {};
  tagDocs.forEach((t) => {
    tagDocMap[t._id] = t;
  });

  // subTheme id -> its annotation-unit row (count + percentage), keyed the
  // same way the groups below are so the two line up.
  const annoCountBySubTheme = {};
  (subThemeAnnotationCounts || []).forEach((row) => {
    const key = row.value === NA_BUCKET ? UNGROUPED_KEY : String(row.value);
    annoCountBySubTheme[key] = {
      count: row.count || 0,
      percentage: row.percentage || 0,
    };
  });

  const groups = {};
  annotationData.forEach((row) => {
    const tagDoc = tagDocMap[row.value];
    const firstSubTheme = tagDoc?.tags?.subThemes?.data?.[0];
    const key = firstSubTheme?._id || UNGROUPED_KEY;
    const display =
      firstSubTheme && (subThemeDisplayMap[firstSubTheme._id] || firstSubTheme.display)
        ? subThemeDisplayMap[firstSubTheme._id] || firstSubTheme.display
        : "Ungrouped";
    const role = firstSubTheme ? subThemeRoleMap[firstSubTheme._id] || "thematic" : "thematic";
    if (!groups[key]) {
      const subThemeRow = annoCountBySubTheme[key];
      groups[key] = {
        subThemeId: key,
        subThemeDisplay: display,
        taxonomyRole: role,
        total: subThemeRow?.count || 0,
        percentage: subThemeRow?.percentage || 0,
        tags: [],
      };
    }
    groups[key].tags.push(row);
  });

  return Object.values(groups)
    .map((g) => ({ ...g, tags: [...g.tags].sort((a, b) => (b.count || 0) - (a.count || 0)) }))
    .sort((a, b) => b.total - a.total);
};
